import { useState, useEffect, useMemo } from "react";
import { Link, Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useSacredAdmin } from "@/hooks/useSacredAdmin";
import { useTattvaGraph } from "@/hooks/useTattvaGraph";
import {
  fetchTattvaIndex,
  fetchTattvaNode,
  fetchTattvaDebug,
} from "@/lib/tattva/tattva-api";
import { GraphVisualizer } from "@/components/tattva/GraphVisualizer";
import { LIBRARY_EXPECTED_RELATIONS } from "@/lib/tattva/tattva-expected-relations";
import { Loader2, ArrowLeft, RefreshCw } from "lucide-react";
import { getEditorAuth } from "@/lib/sacred-library/sacred-library-api";

function missingRelations(byRelation, node) {
  if (!node) return [];
  const lib = new Set(["deity", "festival", "vrata", "story", "sadhana", "mantra"]);
  if (!lib.has(node.type)) return [];
  return LIBRARY_EXPECTED_RELATIONS.filter((rel) => !(byRelation?.[rel]?.length));
}

export function TattvaTestPage() {
  const { isAdmin, editorUser } = useSacredAdmin();
  const [nodeId, setNodeId] = useState("deity:shiva");
  const [index, setIndex] = useState([]);
  const [raw, setRaw] = useState(null);
  const [debug, setDebug] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { data: related, loading: relatedLoading, reload: reloadRelated } = useTattvaGraph(nodeId);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [idx, nodeRes, dbg] = await Promise.all([
        fetchTattvaIndex().catch(() => []),
        fetchTattvaNode(nodeId),
        fetchTattvaDebug().catch(() => null),
      ]);
      setIndex(idx);
      setRaw(nodeRes);
      setDebug(dbg);
      reloadRelated();
    } catch (e) {
      setError(e?.response?.data?.detail || "Load failed — admin login required");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isAdmin && getEditorAuth().token) load();
  }, [isAdmin, nodeId]);

  const missing = useMemo(
    () => missingRelations(related?.by_relation, raw?.node),
    [related, raw]
  );

  const vizNodes = related?.nodes?.length ? related.nodes : raw?.node ? [raw.node] : [];
  const vizEdges = related?.edges?.length ? related.edges : [];

  if (!getEditorAuth().token) {
    return (
      <div className="min-h-screen py-24 px-4">
        <p className="text-muted-foreground">
          <Link to="/admin/sacred" className="text-primary underline">
            Sign in
          </Link>{" "}
          as admin first.
        </p>
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/admin/sacred" replace />;
  }

  return (
    <div className="min-h-screen py-24 px-4" data-testid="tattva-test-page">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin/sacred">
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin
            </Link>
          </Button>
          <h1 className="font-heading text-2xl">Tattva Graph Test</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Admin-only QA page. Console: <code className="text-xs bg-muted px-1 rounded">testTattva(&quot;deity:shiva&quot;)</code>
          {editorUser && <> · {editorUser.email}</>}
        </p>

        <Card>
          <CardContent className="pt-6 flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label>Node id</Label>
              <Input value={nodeId} onChange={(e) => setNodeId(e.target.value)} list="tattva-node-ids" />
              <datalist id="tattva-node-ids">
                {index.map((n) => (
                  <option key={n.id} value={n.id} />
                ))}
              </datalist>
            </div>
            <Button type="button" onClick={load} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Reload
            </Button>
          </CardContent>
        </Card>

        {error && <p className="text-sm text-destructive">{error}</p>}

        {debug?.stats && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Graph integrity (debug API)</CardTitle>
            </CardHeader>
            <CardContent className="text-xs font-mono space-y-1">
              <p>nodes: {debug.stats.node_count} · edges: {debug.stats.edge_count}</p>
              <p>orphans: {debug.stats.orphan_count} · invalid: {debug.stats.invalid_edge_count}</p>
              {debug.invalid_edges?.length > 0 && (
                <pre className="mt-2 p-2 bg-muted rounded overflow-auto max-h-32">
                  {JSON.stringify(debug.invalid_edges.slice(0, 10), null, 2)}
                </pre>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Raw JSON — node</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="text-xs overflow-auto max-h-64 p-2 bg-muted rounded">
                {JSON.stringify(raw, null, 2)}
              </pre>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Related subgraph</CardTitle>
            </CardHeader>
            <CardContent>
              {relatedLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <pre className="text-xs overflow-auto max-h-64 p-2 bg-muted rounded">
                  {JSON.stringify(
                    {
                      anchor_id: related?.anchor_id,
                      node_count: related?.nodes?.length,
                      by_relation: Object.fromEntries(
                        Object.entries(related?.by_relation || {}).map(([k, v]) => [k, v.map((n) => n.id)])
                      ),
                    },
                    null,
                    2
                  )}
                </pre>
              )}
            </CardContent>
          </Card>
        </div>

        {missing.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Missing connections (library QA)</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {missing.map((rel) => (
                <Badge key={rel} variant="destructive">
                  {rel}
                </Badge>
              ))}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Visualization</CardTitle>
          </CardHeader>
          <CardContent>
            <GraphVisualizer nodes={vizNodes} edges={vizEdges} height={360} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
